import { marked } from 'marked';

import { htmlToMarkdown } from '@eluve/markdown-utils';
import { convertMarkdownToPlainText } from '@eluve/utils';

export const useCopyContentElement = ({
  element,
  markdownContent,
  onCopied,
}: {
  element?: HTMLElement | null | undefined;
  markdownContent?: string;
  onCopied?: () => void;
}) => {
  return async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    let htmlContent = '';
    let plainText = '';

    if (element) {
      const clonedElement = element.cloneNode(true) as HTMLElement;
      clonedElement.querySelectorAll('button').forEach((a) => a.remove());

      // Normalize task list HTML for universal clipboard compatibility.
      // Process innermost lists first (reverse) so nested items are
      // normalized before their parent's innerHTML is read.
      Array.from(clonedElement.querySelectorAll('ul[data-type="taskList"]'))
        .reverse()
        .forEach((ul) => {
          ul.querySelectorAll(':scope > li').forEach((li) => {
            const isChecked = li.getAttribute('data-checked') === 'true';
            li.setAttribute('data-type', 'taskItem');

            const contentDiv = li.querySelector(':scope > div');
            const textContent = contentDiv?.innerHTML ?? li.textContent ?? '';

            li.innerHTML = `<input type="checkbox"${isChecked ? ' checked' : ''} disabled>${textContent}`;
          });
        });

      htmlContent = clonedElement.innerHTML;
      plainText = markdownContent
        ? convertMarkdownToPlainText(markdownContent)
        : convertMarkdownToPlainText(htmlToMarkdown(htmlContent));
    } else if (markdownContent) {
      htmlContent = marked(markdownContent);
      plainText = convertMarkdownToPlainText(markdownContent);
    } else {
      return;
    }

    // Copy both text/html and text/plain in a single clipboard operation.
    // Using a copy event listener ensures both formats are set atomically,
    // unlike calling copy() twice (which overwrites the first call) or
    // navigator.clipboard.write (which requires permissions not always available).
    const handler = (event: ClipboardEvent) => {
      event.preventDefault();
      event.clipboardData?.setData('text/html', htmlContent);
      event.clipboardData?.setData('text/plain', plainText);
    };
    document.addEventListener('copy', handler);
    document.execCommand('copy');
    document.removeEventListener('copy', handler);

    onCopied?.();
    e.currentTarget?.blur();
  };
};
